import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

export const ChatSection = ({ woId, role, auth }) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  const loadChatMessages = async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${woId}/messages`);
      setChatMessages(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadChatMessages(); }, [woId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // WebSocket for real-time chat
  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications`;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'work_order_message' && data.work_order_id === woId) {
            setChatMessages(prev => [...prev, {
              id: Date.now().toString(),
              sender_name: data.sender_name,
              sender_role: data.sender_role,
              message: data.message,
              created_at: data.timestamp,
            }]);
            if (data.sender_role !== role) {
              playMessageSound();
            }
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, [woId, role]);

  const playMessageSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = 'sine';
      osc2.frequency.value = 1100;
      gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
      osc2.start(ctx.currentTime + 0.15);
      osc2.stop(ctx.currentTime + 0.45);
    } catch { /* ignore */ }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatSending) return;
    setChatSending(true);
    const senderName = auth?.admin ? `${auth.admin.first_name || ''} ${auth.admin.last_name || ''}`.trim() || auth.admin.email : 'Utilisateur';
    try {
      await axios.post(`${API}/work-orders/${woId}/messages`, {
        message: chatInput.trim(), sender_name: senderName, sender_role: role,
      });
      setChatInput('');
    } catch { toast.error('Erreur envoi message'); }
    setChatSending(false);
  };

  const roleColors = {
    admin: 'bg-purple-100 text-purple-800 border-purple-200',
    reception: 'bg-blue-100 text-blue-800 border-blue-200',
    atelier: 'bg-amber-100 text-amber-800 border-amber-200',
  };
  const roleLabels = { admin: 'Admin', reception: 'Reception', atelier: 'Atelier' };

  return (
    <Card className="p-4 rounded-xl space-y-3" data-testid="wo-chat-section">
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
        <MessageCircle className="w-4 h-4" /> Messages internes
        {chatMessages.length > 0 && (
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{chatMessages.length}</span>
        )}
      </h3>

      <div className="max-h-64 overflow-y-auto space-y-2 border border-slate-100 rounded-lg p-2 bg-slate-50/50" data-testid="chat-messages-container">
        {chatMessages.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-4">Aucun message — Commencez la discussion</p>
        ) : chatMessages.map((msg, i) => {
          const isMe = msg.sender_role === role;
          const roleBadge = roleColors[msg.sender_role] || 'bg-slate-100 text-slate-600';
          const roleLabel = roleLabels[msg.sender_role] || msg.sender_role;
          return (
            <div key={msg.id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-2 ${isMe ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200'}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isMe ? 'bg-blue-500 text-blue-100' : roleBadge}`}>{roleLabel}</span>
                  <span className={`text-[10px] ${isMe ? 'text-blue-200' : 'text-slate-400'}`}>{msg.sender_name}</span>
                  <span className={`text-[10px] ${isMe ? 'text-blue-200' : 'text-slate-400'}`}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-CH', {hour:'2-digit', minute:'2-digit'}) : ''}
                  </span>
                </div>
                <p className={`text-sm ${isMe ? 'text-white' : 'text-slate-900'}`}>{msg.message}</p>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div className="flex gap-2">
        <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Ecrire un message..." className="flex-1" data-testid="chat-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }} />
        <Button onClick={sendChatMessage} disabled={!chatInput.trim() || chatSending} size="sm" data-testid="chat-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};
